import React from 'react';
import PropTypes from 'prop-types';

// The orb uses an angular (conic) gradient, which SVG cannot express natively.
// It is painted by an HTML element inside `foreignObject`, scaled down and
// clipped to the orb circle — the same technique the Figma export uses.
const ORB_GRADIENT =
  'conic-gradient(from 90deg, rgba(101, 11, 146, 1) 0deg, rgba(238, 9, 121, 1) 90deg, ' +
  'rgba(220, 69, 36, 1) 180deg, rgba(238, 9, 121, 1) 270deg, rgba(101, 11, 146, 1) 360deg)';

const ORB_GRADIENT_STYLE = {
  background: ORB_GRADIENT,
  height: '100%',
  width: '100%',
  opacity: 1,
};

const HARD_ALPHA_MATRIX = '0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0';

const AiButtonIcon = ({ className = undefined }) => (
  <svg
    className={className}
    width="129"
    height="127"
    viewBox="0 0 129 127"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    focusable="false"
    aria-hidden="true"
  >
    <g opacity="0.32" filter="url(#ai-button-glow)">
      <circle cx="68.8" cy="68.8" r="44.8" fill="url(#ai-button-glow-gradient)" />
    </g>
    <g filter="url(#ai-button-orb-shadow)">
      <g clipPath="url(#ai-button-orb-clip)">
        <g transform="matrix(0 0.032 -0.032 0 68.8008 66.2402)">
          <foreignObject x="-1031.25" y="-1031.25" width="2062.5" height="2062.5">
            <div style={ORB_GRADIENT_STYLE} />
          </foreignObject>
        </g>
      </g>
      <circle cx="68.8008" cy="66.2402" r="31.5" stroke="white" strokeOpacity="0.45" />
    </g>
    <g opacity="0.5" filter="url(#ai-button-highlight-blur)">
      <ellipse
        cx="68.8016"
        cy="52.8004"
        rx="19.2"
        ry="10.88"
        fill="url(#ai-button-highlight-gradient)"
      />
    </g>
    <g filter="url(#ai-button-spark-shadow)">
      <path
        d="M68.8008 56.2402C69.2452 61.9226 71.4674 64.3932 78.8008 66.7402C71.4674 69.0873 69.2452 71.5579 68.8008 77.2402C68.3563 71.5579 66.1341 69.0873 58.8008 66.7402C66.1341 64.3932 68.3563 61.9226 68.8008 56.2402Z"
        fill="white"
      />
    </g>
    <g opacity="0.9" clipPath="url(#ai-button-small-spark-clip)">
      <path
        d="M80.8008 51.2402C80.9786 53.4049 81.8674 54.3461 84.8008 55.2402C81.8674 56.1344 80.9786 57.0755 80.8008 59.2402C80.623 57.0755 79.7341 56.1344 76.8008 55.2402C79.7341 54.3461 80.623 53.4049 80.8008 51.2402Z"
        fill="white"
      />
    </g>
    <g opacity="0.8">
      <path
        d="M58.2207 74.5601C58.3096 75.913 59.254 76.5012 60.7207 77.0601C59.254 77.6189 58.3096 78.2071 58.2207 79.5601C58.1318 78.2071 57.1874 77.6189 55.7207 77.0601C57.1874 76.5012 58.1318 75.913 58.2207 74.5601Z"
        fill="white"
      />
    </g>
    <defs>
      <filter
        id="ai-button-glow"
        x="0"
        y="0"
        width="137.6"
        height="137.6"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
        <feGaussianBlur stdDeviation="12" result="effect1_foregroundBlur" />
      </filter>
      <filter
        id="ai-button-orb-shadow"
        x="16.8008"
        y="23.2402"
        width="104"
        height="104"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values={HARD_ALPHA_MATRIX}
          result="hardAlpha"
        />
        <feMorphology radius="6" operator="erode" in="SourceAlpha" result="effect1_dropShadow" />
        <feOffset dy="9" />
        <feGaussianBlur stdDeviation="13" />
        <feColorMatrix type="matrix" values="0 0 0 0 0.07 0 0 0 0 0.09 0 0 0 0 0.16 0 0 0 0.18 0" />
        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values={HARD_ALPHA_MATRIX}
          result="hardAlpha"
        />
        <feOffset dy="2" />
        <feGaussianBlur stdDeviation="3" />
        <feColorMatrix type="matrix" values="0 0 0 0 0.07 0 0 0 0 0.09 0 0 0 0 0.16 0 0 0 0.1 0" />
        <feBlend mode="normal" in2="effect1_dropShadow" result="effect2_dropShadow" />
        <feBlend mode="normal" in="SourceGraphic" in2="effect2_dropShadow" result="shape" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values={HARD_ALPHA_MATRIX}
          result="hardAlpha"
        />
        <feOffset dy="1" />
        <feGaussianBlur stdDeviation="1" />
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.5 0" />
        <feBlend mode="normal" in2="shape" result="effect3_innerShadow" />
      </filter>
      <filter
        id="ai-button-highlight-blur"
        x="44.6016"
        y="36.9204"
        width="48.4004"
        height="31.7598"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
        <feGaussianBlur stdDeviation="2.5" result="effect1_foregroundBlur" />
      </filter>
      <filter
        id="ai-button-spark-shadow"
        x="51.0801"
        y="49.52"
        width="35"
        height="35"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values={HARD_ALPHA_MATRIX}
          result="hardAlpha"
        />
        <feOffset dy="1" />
        <feGaussianBlur stdDeviation="1.5" />
        <feColorMatrix type="matrix" values="0 0 0 0 0.04 0 0 0 0 0.05 0 0 0 0 0.16 0 0 0 0.4 0" />
        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
        <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape" />
      </filter>
      <clipPath id="ai-button-orb-clip">
        <circle cx="68.8008" cy="66.2402" r="32" />
      </clipPath>
      <linearGradient
        id="ai-button-glow-gradient"
        x1="36.8"
        y1="36.8"
        x2="100.8"
        y2="100.8"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#E7004F" />
        <stop offset="1" stopColor="#005ED9" />
      </linearGradient>
      <linearGradient
        id="ai-button-highlight-gradient"
        x1="68.8016"
        y1="41.9204"
        x2="68.8016"
        y2="63.6804"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="white" />
        <stop offset="1" stopColor="white" stopOpacity="0.2" />
      </linearGradient>
      <clipPath id="ai-button-small-spark-clip">
        <rect width="8" height="8" fill="white" transform="translate(76.8008 51.2402)" />
      </clipPath>
    </defs>
  </svg>
);

AiButtonIcon.propTypes = {
  className: PropTypes.string,
};

export default AiButtonIcon;
